/* eslint-disable no-restricted-globals */
import React, { useEffect, useState } from 'react';
import { Save, RefreshCw, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import { settingsApi, telegramLinkApi } from '../api';
import { useToast } from '../components/ui/ToastProvider';
import type { Settings as SettingsType } from '../types';

export const Settings: React.FC = () => {
  const toast = useToast();
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    usd_rate: '',
    shop_name: '',
    shop_address: '',
    shop_phone: '',
    receipt_footer: '',
  });

  useEffect(() => {
    settingsApi.get().then((res) => {
      setSettings(res.data);
      setFormData({
        usd_rate: res.data.usd_rate,
        shop_name: res.data.shop_name,
        shop_address: res.data.shop_address,
        shop_phone: res.data.shop_phone,
        receipt_footer: res.data.receipt_footer,
      });
      setLoading(false);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await settingsApi.update(formData);
      setSettings(res.data);
      toast.success('Sozlamalar saqlandi');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="page-bar text-xl sm:text-2xl font-bold text-gray-900">Sozlamalar</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Currency & Pricing */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Dollar kursi</h2>
          <div className="max-w-xs">
            <label className="label">1 USD = ? UZS</label>
            <input
              type="number"
              name="usd_rate"
              className="input"
              value={formData.usd_rate}
              onChange={handleChange}
              step="0.01"
              min="1"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Dollarda kiritilgan tan narxlarni so'mga aylantirish uchun ishlatiladi.
              Sotish narxlariga ta'sir qilmaydi — ular har bir tovarda qo'lda belgilanadi.
            </p>
          </div>
        </div>

        {/* Shop Info */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Do'kon ma'lumotlari</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Do'kon nomi</label>
              <input
                type="text"
                name="shop_name"
                className="input"
                value={formData.shop_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="label">Manzil</label>
              <input
                type="text"
                name="shop_address"
                className="input"
                value={formData.shop_address}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="label">Telefon</label>
              <input
                type="text"
                name="shop_phone"
                className="input"
                value={formData.shop_phone}
                onChange={handleChange}
                placeholder="+998..."
              />
            </div>
          </div>
        </div>

        {/* Receipt Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Chek sozlamalari</h2>
          <div>
            <label className="label">Chek pastki qismi</label>
            <textarea
              name="receipt_footer"
              className="input"
              rows={3}
              value={formData.receipt_footer}
              onChange={handleChange}
              placeholder="Xaridingiz uchun rahmat!"
            />
            <p className="text-sm text-gray-500 mt-1">Chekning eng pastida chiqadigan matn</p>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn btn-primary flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <RefreshCw className="animate-spin" size={20} />
            ) : (
              <Save size={20} />
            )}
            Saqlash
          </button>
        </div>
      </form>

      {/* Telegram ulash — Mini App sotuvchini shu orqali taniydi */}
      <TelegramLinkCard />

      {/* Last updated */}
      {settings?.updated_at && (
        <p className="text-sm text-gray-500 text-center">
          Oxirgi yangilanish: {new Date(settings.updated_at).toLocaleString('uz-UZ')}
        </p>
      )}
    </div>
  );
};

const TelegramLinkCard: React.FC = () => {
  const [linked, setLinked] = useState<boolean | null>(null);
  const [telegramId, setTelegramId] = useState<number | null>(null);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    telegramLinkApi.status()
      .then((res) => {
        setLinked(res.data.linked);
        setTelegramId(res.data.telegram_id);
      })
      .catch(() => setLinked(false));
  };

  useEffect(load, []);

  const handleCreate = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await telegramLinkApi.create();
      setLink(res.data.link);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Havola yaratib bo\'lmadi');
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Telegram ulanishini uzmoqchimisiz?')) return;
    setBusy(true);
    try {
      await telegramLinkApi.unlink();
      setLink('');
      load();
    } finally {
      setBusy(false);
    }
  };

  if (linked === null) return null;

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Send size={18} className="text-primary-800" />
        <h2 className="text-lg font-semibold">Telegram ulanishi</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Mini App'da tovarlaringizni ko'rish uchun Telegram akkauntingiz shu hisobga
        ulangan bo'lishi kerak.
      </p>

      {linked ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-success-700">
            <CheckCircle size={18} />
            <span className="font-medium">Ulangan</span>
            <span className="text-gray-400 font-mono text-sm">ID: {telegramId}</span>
          </div>
          <button onClick={handleUnlink} disabled={busy} className="btn btn-secondary">
            Uzish
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-orange-600">
            <AlertTriangle size={18} />
            <span>Ulanmagan — Mini App'da tovarlar ko'rinmaydi</span>
          </div>

          {link ? (
            <div className="rounded-lg bg-primary-50 border border-primary-200 p-3 space-y-2">
              <p className="text-sm text-primary-900">
                Quyidagi havolani <b>Telegram'da oching</b> (30 daqiqa amal qiladi):
              </p>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <Send size={16} />
                Telegram'da ochish
              </a>
              <p className="text-xs text-gray-500 break-all">{link}</p>
              <button onClick={load} className="text-sm text-primary-800 underline">
                Ulangach — holatni yangilash
              </button>
            </div>
          ) : (
            <button onClick={handleCreate} disabled={busy} className="btn btn-primary flex items-center gap-2">
              <Send size={18} />
              {busy ? 'Yaratilmoqda...' : 'Telegram\'ni ulash'}
            </button>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};
